/**
 * Neon 2.5D style constants. Single source of truth for the Batch-2
 * renderer overhaul — layer counts, z-step, bloom, translucency, camera
 * pitch, bob, bullet speed, and player skill live here. Hot-tweak in
 * one place; every other file reads from this module.
 *
 * CLAUDE.md architecture rule 5 (single tuning source) applies.
 */

// ---------- 2.5D stacking (world geometry) ----------

/** Screen-space pixels of vertical offset per stack layer at base zoom.
 *  Bigger = taller-looking walls; too big = stack detaches from the base
 *  tile visually. 7 gives wall stacks that read as chest-to-head height. */
export const LAYER_Z_STEP_PX = 7;

/** Layer counts per tile category (ground is always layer 0). */
export const LAYERS = {
  floor: 1,
  fullWall: 3,
  halfWall: 2,
  coverLight: 1,
  coverHeavy: 2,
  entity: 1,
} as const;

/** Ground-level tiles stay opaque; every raised layer is translucent so the
 *  map behind ridges stays legible. Values in [0, 1]. */
export const STACK_ALPHA_GROUND = 1.0;
export const STACK_ALPHA_RAISED = 0.85;

/** Each raised layer darkens the base tile color by this fraction (0..1). */
export const STACK_DARKEN_PER_LAYER = 0.14;

/** Multiplier on halo/bloom strength for raised layers vs. ground. Hotter
 *  glow on the raised layers makes wall ridges pop. */
export const STACK_HALO_BOOST = 1.6;

// ---------- Bloom / neon glow ----------

/** Bloom halo alpha for a raised map-layer glyph (walls above ground).
 *  Now feeds a real BlurFilter — value is the base opacity of the source
 *  sprite that gets blurred into a glow. */
export const BLOOM_ALPHA = 0.75;

/** Halo scale — bloom sprite drawn at N× glyph size before blur. */
export const BLOOM_SCALE = 1.3;

/** Extra bloom boost for "hero" glyphs (player @, enemies, pickups). */
export const BLOOM_HERO_BOOST = 1.4;

/** BlurFilter strength on the bloom layers. Higher = softer wider glow. */
export const BLOOM_BLUR_STRENGTH = 10;

/** BlurFilter quality (Pixi shader pass count). Higher = smoother but
 *  more expensive; 4 is a good middle. */
export const BLOOM_BLUR_QUALITY = 4;

// ---------- CRT overlay ----------

/** Scanline dark-band alpha. 0 disables scanlines. */
export const SCANLINE_ALPHA = 0.12;

/** Scanline period in device-independent pixels (band + gap). */
export const SCANLINE_PERIOD_PX = 4;

/** Random per-glyph brightness jitter amplitude (0..1). 0 disables. */
export const GLYPH_JITTER_AMPLITUDE = 0.06;

// ---------- Ambient background noise ----------

/** Fraction of empty background tiles that get a scattered noise glyph. */
export const AMBIENT_NOISE_DENSITY = 0.14;

/** Alpha multiplier applied to noise glyphs. */
export const AMBIENT_NOISE_ALPHA = 0.35;

/** Character pool for the ambient noise field. */
export const AMBIENT_NOISE_GLYPHS: readonly string[] = ['*', ':', '-', '=', '~', '.', '·'];

// ---------- Camera ----------

/** Perspective tilt of the world plane (degrees below horizontal). 90 = flat
 *  top-down, 0 = side-on. Spec asks for 60-70 range. */
export const CAMERA_PITCH_DEG = 66;

/** How many tiles the camera trails behind the followed entity's facing. */
export const CAMERA_TRAIL_TILES = 3.5;

/** Lerp factor for camera→focused-target smoothing per tick. */
export const CAMERA_FOCUS_LERP = 0.18;

/** Lerp factor for camera→trail-target smoothing per tick. */
export const CAMERA_TRAIL_LERP = 0.14;

/** 8-cardinal heading rotation snap time (seconds). */
export const CAMERA_ROTATE_SNAP_SEC = 0.22;

// ---------- Walk bob ----------

/** Peak vertical bob amplitude in device-independent pixels. */
export const BOB_AMPLITUDE_PX = 1.6;

/** Base bob frequency at walk speed, Hz. */
export const BOB_FREQ_BASE_HZ = 6.5;

/** Dead-player ghost "float": a slow, gentle up/down drift (both directions,
 *  unlike the ground bob) instead of a leg-driven walk bob. */
export const GHOST_FLOAT_HZ = 0.9;
export const GHOST_FLOAT_PX = 3.2;
/** Ghost silhouette tint + opacity — a pale, translucent spirit. */
export const GHOST_TINT = 0xbfe8ff;
export const GHOST_ALPHA = 0.55;

/** Bob-frequency multiplier per (tiles/sec above walk). Faster = quicker bob. */
export const BOB_FREQ_SPEED_MULT = 0.6;

// ---------- Legs walk cycle ----------
// The player's legs sprite alternates between two katakana frames (ハ ↔ メ)
// while moving; the cadence scales with travel speed so a sprint's steps
// flicker faster than a walk's. Frozen (not reset) on stop.

/** Legs walk-frame glyphs — frame 0 (also the idle/rest frame) and frame 1. */
export const LEG_ANIM_FRAMES: readonly [string, string] = ['ハ', 'メ'];

/** Base leg step-cycle frequency at low speed, Hz (full ハ→メ→ハ cycles/sec). */
export const LEG_ANIM_BASE_HZ = 2.6;

/** Leg step-frequency multiplier per (tile/sec of travel speed). */
export const LEG_ANIM_SPEED_MULT = 0.5;

// ---------- Held-weapon suit (focus mode) ----------
// In focus mode the player "holds up" the active weapon's card suit as a glyph
// on the character itself: ♣ Main · ♦ Side · ♠ Melee · ♥ Heal. Weapons rotate
// to point at the locked target; the heal ♥ appears upright and never rotates.

/** Card-suit glyph per weapon slot, matching the taskbar + stance display. */
export const WEAPON_SUIT_GLYPHS: readonly [string, string, string, string] = ['♣', '♦', '♠', '♥'];

/** Tint per suit on the in-game character. Only the heal ♥ is red; the three
 *  weapons (♣ ♦ ♠) share the same pale steel so the side weapon reads like the
 *  others rather than as a threat/health colour. */
export const WEAPON_SUIT_TINTS: readonly [number, number, number, number] = [
  0xd6e2f0, 0xd6e2f0, 0xd6e2f0, 0xff5b6e,
];

/** How far (in tile-widths) the held suit sits out from the torso. */
export const WEAPON_SUIT_REACH = 0.62;

// ---------- Bullets ----------

/** Bullet travel speed in tiles per second. Fast enough to feel snappy but
 *  visibly not hitscan. */
export const BULLET_SPEED_TILES_PER_SEC = 26;

/** Bullet glyph. */
export const BULLET_GLYPH = ',';

/** Bullet trail length in device-independent pixels. */
export const BULLET_TRAIL_PX = 10;

/** Bullet trail alpha at the tail end. */
export const BULLET_TRAIL_ALPHA = 0.45;

/** Maximum age of a bullet in seconds — safety cap in case it misses
 *  everything and flies off-map. */
export const BULLET_MAX_AGE_SEC = 3.0;

// ---------- Player skill / reticule ----------

/** Player's marksmanship skill in [0, 1]. Drives reticule drift radius +
 *  frequency. 0 = flailing, 1 = laser. Spec: 0.75. */
export const PLAYER_SKILL = 0.75;

/** Body-panel half-width in "body units" (torso = 0..1). Reticule drifts
 *  within a radius scaled by (1 - PLAYER_SKILL). */
export const RETICLE_BODY_HALF_WIDTH = 0.55;

/** Maximum reticule drift radius when skill = 0, in body units. */
export const RETICLE_DRIFT_MAX_RADIUS = 0.6;

/** Maximum reticule drift speed when skill = 0, radians/sec. */
export const RETICLE_DRIFT_MAX_SPEED = 3.2;

/** Derived: current drift radius (body units). */
export const RETICLE_DRIFT_RADIUS = (1 - PLAYER_SKILL) * RETICLE_DRIFT_MAX_RADIUS;

/** Derived: current drift angular speed (rad/sec). */
export const RETICLE_DRIFT_SPEED = (1 - PLAYER_SKILL) * RETICLE_DRIFT_MAX_SPEED;

// ---------- Neon palette ----------

/** Cyan/teal — player, friendly UI. */
export const COLOR_PLAYER = 0x22e6ff;
/** Green — positive stats, allies. */
export const COLOR_ALLY = 0x66ff77;
/** Yellow/orange/amber — highlights, targeting, pickups, warnings. */
export const COLOR_WARN = 0xffb84a;
/** Magenta/pink — danger accents, enemy eyes/details. */
export const COLOR_ENEMY_ACCENT = 0xff3caa;
/** Red — alerts, damage. */
export const COLOR_ALERT = 0xff3355;
/** Dim blue/grey — environment, floor. */
export const COLOR_ENVIRONMENT = 0x5a6a86;
/** Dim blue/purple — ambient background noise. */
export const COLOR_AMBIENT_NOISE = 0x3a4a7a;

// ---------- Compass ----------

/** Alpha of a focused-entity glow on the compass bar. */
export const COMPASS_FOCUS_GLOW_ALPHA = 0.85;
